import React, { useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { sendContactMessage } from '../../api/client';

const EMPTY = { name: '', email: '', message: '' };

/**
 * Footer "Stay in touch" form, posting to `POST /api/contact`.
 *
 * Validates client-side for fast feedback, but treats the server's field-level
 * errors as authoritative and merges them back into the same error state.
 */
export default function ContactForm() {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(null); // { type: 'ok'|'err', text }
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    // Clear the field's error as soon as the person starts fixing it.
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const validate = () => {
    const next = {};
    if (!values.name.trim()) next.name = 'Please tell us your name.';
    if (!values.email.trim()) {
      next.email = 'Please enter your email address.';
    } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(values.email.trim())) {
      next.email = 'That doesn’t look like a valid email address.';
    }
    if (!values.message.trim()) next.message = 'Please write a short message.';
    return next;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus(null);

    const clientErrors = validate();
    if (Object.keys(clientErrors).length) {
      setErrors(clientErrors);
      return;
    }

    setSubmitting(true);
    try {
      const result = await sendContactMessage(values);
      setStatus({ type: 'ok', text: result.message || 'Thanks — message sent.' });
      setValues(EMPTY);
      setErrors({});
    } catch (err) {
      // A 400 carries per-field reasons; anything else is a transport failure.
      if (err.body?.errors) {
        setErrors(err.body.errors);
        setStatus({ type: 'err', text: 'Please check the highlighted fields.' });
      } else {
        setStatus({
          type: 'err',
          text: 'Couldn’t send that just now. Please try again, or email hello@mindlyfriend.com.',
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fieldClass = (name) =>
    `mm-contact__control ${errors[name] ? 'mm-contact__control--invalid' : ''}`;

  return (
    <form className="mm-contact" onSubmit={handleSubmit} noValidate>
      <div className="mb-3">
        <label className="mm-sr-only" htmlFor="contact-email">
          Email address
        </label>
        <input
          id="contact-email"
          name="email"
          type="email"
          className={fieldClass('email')}
          placeholder="Email address"
          value={values.email}
          onChange={handleChange}
          aria-invalid={Boolean(errors.email)}
          aria-describedby={errors.email ? 'contact-email-error' : undefined}
        />
        {errors.email && (
          <span className="mm-contact__error" id="contact-email-error">
            {errors.email}
          </span>
        )}
      </div>

      <div className="mb-3">
        <label className="mm-sr-only" htmlFor="contact-name">
          Name
        </label>
        <input
          id="contact-name"
          name="name"
          type="text"
          className={fieldClass('name')}
          placeholder="Name"
          value={values.name}
          onChange={handleChange}
          aria-invalid={Boolean(errors.name)}
          aria-describedby={errors.name ? 'contact-name-error' : undefined}
        />
        {errors.name && (
          <span className="mm-contact__error" id="contact-name-error">
            {errors.name}
          </span>
        )}
      </div>

      <div className="mb-3">
        <label className="mm-sr-only" htmlFor="contact-message">
          Message
        </label>
        <textarea
          id="contact-message"
          name="message"
          rows={4}
          className={fieldClass('message')}
          placeholder="Message"
          value={values.message}
          onChange={handleChange}
          aria-invalid={Boolean(errors.message)}
          aria-describedby={errors.message ? 'contact-message-error' : undefined}
        />
        {errors.message && (
          <span className="mm-contact__error" id="contact-message-error">
            {errors.message}
          </span>
        )}
      </div>

      <Button type="submit" className="px-4" disabled={submitting}>
        {submitting ? (
          <>
            <Spinner animation="border" size="sm" className="me-2" aria-hidden="true" />
            Sending…
          </>
        ) : (
          'Send message'
        )}
      </Button>

      {status && (
        <p
          className={`mm-contact__status mm-contact__status--${status.type === 'ok' ? 'ok' : 'err'}`}
          role="status"
          aria-live="polite"
        >
          {status.text}
        </p>
      )}
    </form>
  );
}
